import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { apiPost } from "../api";
import { printShippingLabels } from "../labelFlow";
import type { PortalConfig } from "../config";
import type { Lookups, TicketRow } from "../types";
import { confirmBox } from "../confirm";

interface Props {
  config: PortalConfig;
  lookups: Lookups | null;
  selected: TicketRow[];
  onDone: () => void;
  onExport: () => void;
  onClear: () => void;
}


/** What staff can do with the selected tickets. */
export function BulkActions({ config, lookups, selected, onDone, onExport, onClear }: Props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const token = auth.user?.id_token ?? "";
  const [statusId, setStatusId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: React.ReactNode } | null>(null);
  const ticketNos = selected.map((t) => t.ticketNo);
  const n = selected.length;
  const plural = (k: number, word: string) => `${k} ${word}${k === 1 ? "" : "s"}`;

  async function run(name: string, fn: () => Promise<React.ReactNode>) {
    setBusy(name);
    setNotice(null);
    try {
      setNotice({ kind: "ok", text: await fn() });
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  const changeStatus = async () => {
    const status = lookups?.statuses.find((s) => String(s.id) === statusId);
    if (!status) return;
    if (!(await confirmBox(`Change ${plural(n, "ticket")} to "${status.name}"?\n\nNo email or text is sent to customers.`, { title: "Change status", ok: "Change status" }))) return;
    void run("status", async () => {
      const r = await apiPost<{ changed: number }>(config, token, "/tickets/actions/status", { ticketNos, statusId: status.id });
      onDone();
      setStatusId("");
      return `${plural(r.changed, "ticket")} moved to ${status.name}${r.changed < n ? ` (${n - r.changed} were already there)` : ""}.`;
    });
  };

  const markRead = () => void run("read", async () => {
    const r = await apiPost<{ messages: number; texts: number }>(config, token, "/tickets/actions/mark-read", { ticketNos });
    onDone();
    return `Marked ${plural(r.messages, "message")} read and cleared ${plural(r.texts, "text")}.`;
  });

  const markUnread = () => void run("unread", async () => {
    const r = await apiPost<{ marked: number }>(config, token, "/tickets/actions/mark-unread", { ticketNos });
    onDone();
    return `Marked ${plural(r.marked, "ticket")} unread.`;
  });

  const printLabels = () => {
    // Open the tab now (a tab opened after waiting is often blocked), then point it at the PDF.
    const tab = window.open("", "_blank");
    void run("labels", async () => {
      const r = await printShippingLabels(config, token, ticketNos, tab, (t) => setNotice({ kind: "ok", text: t }));
      onDone();
      // Always reachable, even when the new tab was blocked or can't show PDFs (owner, 2026-11-04).
      return r.pdfUrl ? <>{r.text} <a href={r.pdfUrl} download="WRP-shipping-labels.pdf" target="_blank" rel="noreferrer">Download labels (PDF)</a></> : r.text;
    });
  };

  const statuses = lookups?.statuses.filter((s) => s.isActive) ?? [];

  return (
    <div className="bulk-bar" role="toolbar" aria-label="Selected tickets">
      <strong>{plural(n, "ticket")} selected</strong>
      <button type="button" disabled={!!busy} onClick={() => navigate("/print/invoices", { state: { ticketNos } })}>
        Print invoices
      </button>
      <button type="button" disabled={!!busy} onClick={printLabels}
        title="Buys labels through Shippo where a ticket has none (you confirm the cost first), then prints for each ticket the label to ship the watch back and the return label that goes in the box">
        {busy === "labels" ? "Preparing labels…" : "Print shipping labels"}
      </button>
      <span className="split">
        <select value={statusId} onChange={(e) => setStatusId(e.target.value)} aria-label="New status" disabled={!!busy}>
          <option value="">Change status to…</option>
          {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button type="button" disabled={!statusId || !!busy} onClick={() => void changeStatus()}>{busy === "status" ? "Saving…" : "Apply"}</button>
      </span>
      <button type="button" disabled={!!busy} onClick={markRead}>{busy === "read" ? "Saving…" : "Mark as read"}</button>
      <button type="button" disabled={!!busy} onClick={markUnread}>{busy === "unread" ? "Saving…" : "Mark unread"}</button>
      <button type="button" disabled={!!busy} onClick={onExport}>Export CSV</button>
      <button type="button" className="link" onClick={onClear}>Clear selection</button>
      {notice && <div className={notice.kind === "error" ? "bulk-notice error" : "bulk-notice"} role="status">{notice.text}</div>}
    </div>
  );
}
